import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import Header from 'components/Header/Header';
import { setLoadVpcSheetVisible, showOpenDesignSheet } from 'ducks/sheets';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import { useIntl } from 'react-intl';
import useHideLoadingScreen from 'hooks/useHideLoadingScreen';
import { isKiosk, isMobile } from 'utils/platform';
import RtlStyles from 'i18n/rtl';
import BannerMessage from 'components/BannerMessage/BannerMessage';
import media from 'ui/media';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import useResetPlanner from 'hooks/useResetPlanner';
import { AppRoutes } from 'routes';
import {
  reportBookRemotePlanning,
  reportLandingPageSelectStart,
  reportToggleModal
} from 'analytics/reporters';
import { getQuery, getRange } from 'utils/queryUtils';
import { setLastFocusedElementId } from 'utils/focusRestore';
import { useSceneHandler } from 'contexts/sceneHandler';
import { useAppDispatch } from 'ducks/hooks';
import LanguageSelector from 'ui/LanguageSelector/LanguageSelector';
import { getLocale } from 'i18n/i18nUtils';
import Card from '@ingka/card';
import { useServiceSettings } from 'contexts/serviceSettings';
import { useSelector } from 'react-redux';
import { ReportCurrentPageEnum } from 'components/ProductSidebar/HomeDelivery/HomeDelivery';
import { getVpcCode } from 'ducks/vpc';
import { getShowBannerMessage } from 'ducks/layout';
import Avatar from '@ingka/avatar';

import LandingPageGallery from './LandingPageGallery';
import ContentMargins from 'components/ContentMargins';

import '@ingka/svg-icon/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/image/dist/style.css';
import '@ingka/aspect-ratio-box/dist/style.css';
import '@ingka/card/dist/style.css';
import '@ingka/focus/dist/style.css';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { getKioskIntegration, isGalleryActive, isUpptackaActive } from 'utils/integration';
import { getUserName } from 'ducks/userInfo';
import Body from 'ui/Typography/Body';
import Box from 'ui/Box';

const Container = styled.div`
  width: 100%;
  height: 100%;
  line-height: 1.571;
  ${media.mobile`
    margin-bottom: 2rem;
  `}
  ${media.tablet`
    margin-bottom: 2rem;
  `}
`;

const StyledCard = styled(Card)`
  flex-direction: column !important;

  .card__info {
    justify-content: space-between;
  }
`;

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface GridProps {
  columns: number;
  slim: boolean;
}

const Grid = styled.div<GridProps>`
  display: grid;
  grid-template-columns: ${props => `repeat(${props.columns}, 1fr)`};
  grid-auto-rows: min-content;
  gap: 16px 16px;
  padding-bottom: ${props => `${props.slim ? '0px' : '20px'}`};
  margin: ${props => `${props.slim ? '0px 180px' : '50px 180px'}`};
  max-width: 1600px;
  width: 100%;

  .aspect-ratio-box[class*='--wide'] {
    padding-bottom: 77%;
  }

  .card {
    &:hover {
      cursor: pointer;
    }
  }

  @media screen and (max-width: 1600px) {
    margin: ${props => `${props.slim ? '0px 180px' : '40px 180px'}`};
  }

  @media screen and (max-width: 1300px) {
    margin: ${props => `${props.slim ? '0px 20px' : '0px'}`};
  }

  @media screen and (max-width: 800px) {
    grid-template-columns: 1fr;
  }

  ${media.mobile`
    grid-template-columns: 1fr;
    margin: 0;
  `}
`;

const CarouselContainer = styled.div`
  display: block;
  max-width: 1600px;
  width: 100%;
  margin-bottom: 4rem;
`;

const RangeTitleBackgroundContainer = styled.div`
  display: flex;
  position: relative;

  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
    ${media.mobile`
        display: none;
    `}
  }
`;

const TitleWrapper = styled.div`
  z-index: 2;
  margin: 70px 56px;
`;

const TitleWrapperMobile = styled.div`
  z-index: 2;
  margin: 10px 0;
`;

const BannerMessageWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto 1rem;
`;

const SimpleGrid = styled.div`
  display: grid;
  grid-template-columns: ${isMobile() ? '1fr' : '1fr 1fr'};
  grid-gap: 16px;
  grid-template-rows: max-content;

  .aspect-ratio-box[class*='--wide'] {
    padding-bottom: 117%;
    ${media.mobile`
  padding-bottom: 80%;
`}
  }
`;

const ProfileButton = styled(Button)`
  .btn__inner {
    padding-left: 0;
  }
`;

const LandingPage = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const range = getRange();
  const userInfo = useSelector(getUserName);
  const { formatMessage } = useIntl();
  const {
    rangeName,
    dexfSettings,
    dexfSettings: {
      gallery: { experienceGalleryBackURL },
      general: { enableSaveToProfile, bookAppointmentEnabled }
    }
  } = useServiceSettings();

  const showBackToIpexGallery =
    getQuery().get('ipexGallery') === 'true' && experienceGalleryBackURL !== undefined;
  const showLogo = !showBackToIpexGallery;
  const { hasPersistedScene } = useSceneHandler();
  const kioskIntegration = getKioskIntegration();
  const vpcCode = useSelector(getVpcCode);
  const {
    plannerConfig: { landingPageConfig }
  } = usePlannerConfig();
  const showBannerMessage = useSelector(getShowBannerMessage);

  const bookingUrl = dexfSettings.kompis.urls.supportBookingLink;
  const bookingPlannerUrl =
    bookingUrl && bookingUrl.replace(`{vpcCode}`, encodeURIComponent(vpcCode || ''));

  const handleBookAppointment = () => {
    reportBookRemotePlanning({
      currentMenu: ReportCurrentPageEnum.landingPage
    });
    window.open(bookingPlannerUrl, '_blank');
  };

  useResetPlanner();
  useHideLoadingScreen();

  const onGuideClick = () => {
    history.push(AppRoutes.GUIDE);
    reportLandingPageSelectStart({ start: 'guide_me' });
  };

  const onStartClick = () => {
    history.push(AppRoutes.PLANNER);
  };

  const onOpenDesignClick = () => {
    setLastFocusedElementId('open-vpc-btn');
    reportToggleModal({
      modal: enableSaveToProfile ? 'OpenDesignSheet' : 'LoadVpcSheet',
      visible: true,
      page: ReportCurrentPageEnum.landingPage
    });
    enableSaveToProfile
      ? dispatch(showOpenDesignSheet(true))
      : dispatch(setLoadVpcSheetVisible(true));
  };

  const onChangeLanguage = (e: any) => {
    const { origin, pathname, search } = window.location;
    const searchParams = new URLSearchParams(search);
    searchParams.set('locale', e.detail.locale);
    kioskIntegration.languageChange(e.detail.locale);
    window.location.replace(`${origin}${pathname}?${searchParams}`);
  };

  const LandingPageGrid = () => {
    if (!landingPageConfig) return <></>;
    return (
      <SimpleGrid>
        <StyledCard
          title={
            enableSaveToProfile
              ? formatMessage(
                  { id: 'storageone.landingPagePlanningToolHeading' },
                  { range: rangeName.toUpperCase() }
                )
              : formatMessage({
                  id: hasPersistedScene()
                    ? 'storageone.landingPageContinueDesignOrStartFromScratchTitle'
                    : 'storageone.landingPageBtnDesignFromScratch'
                })
          }
          body={
            enableSaveToProfile
              ? formatMessage(
                  { id: 'storageone.landingPagePlanningToolEmptySceneBody' },
                  { range: rangeName.toUpperCase() }
                )
              : formatMessage({
                  id: hasPersistedScene()
                    ? 'storageone.landingPageContinueDesignOrStartFromScratchText'
                    : 'storageone.landingPageBtnDesignFromScratchDescription'
                })
          }
          data-testid="item-design-from-scratch"
          emphasised
          backgroundColor={landingPageConfig.designFromScratch.backgroundColor}
          textColor={landingPageConfig.designFromScratch?.textColor || '#000'}
          imageProps={{ src: landingPageConfig?.designFromScratch.backgroundImgUrl, alt: '' }}
          onClick={onStartClick}
          onKeyUp={e => e.key === 'Enter' && onStartClick()}
          ctaLabel={formatMessage({
            id: 'storageone.landingPageContinueDesignOrStartFromScratchTitle'
          })}
          buttonVariant="icon-primary-inverse"
          imagePosition="leading"
          imageRatio={!isMobile() ? 'square' : 'wide'}
          compact={!isMobile()}
          tabIndex={0}
        />
        {!isMobile() && (
          <StyledCard
            title={
              enableSaveToProfile
                ? formatMessage({
                    id: 'storageone.landingPageOpenSavedDesignTitle'
                  })
                : formatMessage({
                    id: 'storageone.landingPageBtnOpenDesign'
                  })
            }
            body={
              enableSaveToProfile
                ? formatMessage({
                    id: 'storageone.landingPageOpenSavedDesignBody'
                  })
                : formatMessage({
                    id: 'storageone.landingPageBtnOpenDesignDescription'
                  })
            }
            imageProps={{ src: landingPageConfig.openDesign.backgroundImgUrl, alt: '' }}
            data-testid="item-open-design"
            emphasised
            backgroundColor={landingPageConfig.openDesign.backgroundColor}
            ctaLabel={formatMessage({
              id: 'storageone.landingPageBtnOpenDesign'
            })}
            textColor={landingPageConfig.openDesign.textColor}
            buttonVariant="icon-primary-inverse"
            onClick={onOpenDesignClick}
            onKeyUp={e => e.key === 'Enter' && onOpenDesignClick()}
            compact={!isMobile()}
            imagePosition="leading"
            imageRatio="square"
            tabIndex={0}
          />
        )}
      </SimpleGrid>
    );
  };

  return (
    <RtlStyles>
      <ContentMargins>
        <Container data-testid="landing-page">
          <Header
            showLogo={showLogo}
            showBackButton={showBackToIpexGallery}
            backButtonTarget={experienceGalleryBackURL}
          >
            {!isMobile() && enableSaveToProfile && userInfo.userName !== undefined && (
              <ProfileButton
                type={ButtonTypeEnum.tertiary}
                onClick={() => {
                  dispatch(showOpenDesignSheet(true));
                  reportToggleModal({
                    modal: 'OpenDesignSheet',
                    visible: true,
                    page: ReportCurrentPageEnum.accountName
                  });
                }}
                size={'xsmall'}
                buttonStyle={{ paddingLeft: 0, fontWeight: 'normal' }}
                className="header-profile-button"
              >
                <Avatar variant="primary" screenReaderText="" withIcon />
                <Body type={ThemeFontStyleTypeEnum.bodyMedium} pl="xs" bold={false}>
                  {userInfo.userName !== undefined ? `Hej ${userInfo.userName}!` : 'Hej!'}
                </Body>
              </ProfileButton>
            )}
            {isKiosk() && (
              <LanguageSelector
                data-testid="language-selector"
                locale={getLocale()}
                onChange={onChangeLanguage}
                mr="s"
              />
            )}
            {isMobile() && (
              <Button
                dataTestId="open-vpc"
                id="open-vpc-btn"
                onClick={onOpenDesignClick}
                type={ButtonTypeEnum.secondary}
                text={formatMessage({ id: 'storageone.open' })}
                size={isMobile() ? 'small' : 'medium'}
              />
            )}
            {(isUpptackaActive() || isGalleryActive()) && (
              <Button
                dataTestId="exit-application"
                id="exit-application-btn"
                style={{ marginLeft: '0.75rem' }}
                onClick={() => kioskIntegration.exitClicked()}
                type={ButtonTypeEnum.primary}
                text={formatMessage({ id: 'storageone.exitApplication' })}
                size={isMobile() ? 'small' : 'medium'}
              />
            )}
          </Header>
          {showBannerMessage && (
            <BannerMessageWrapper>
              <BannerMessage />
            </BannerMessageWrapper>
          )}
          <GridContainer>
            <Grid columns={2} slim={true}>
              <RangeTitleBackgroundContainer
                style={{
                  backgroundColor: landingPageConfig?.rangeDesign.backgroundColor
                    ? landingPageConfig?.rangeDesign.backgroundColor
                    : ''
                }}
              >
                {!isMobile() && landingPageConfig?.rangeDesign.backgroundImgUrl && (
                  <img src={landingPageConfig?.rangeDesign.backgroundImgUrl} alt="" />
                )}
                {isMobile() ? (
                  <TitleWrapperMobile>
                    <Title
                      type={ThemeFontStyleTypeEnum.headingXLarge}
                      style={{ fontSize: '2.25rem' }}
                    >
                      {formatMessage({
                        id: 'storageone.designSolutionLandingPageTitleStartText'
                      })}

                      <b>{` ${rangeName} `}</b>
                    </Title>
                  </TitleWrapperMobile>
                ) : (
                  <TitleWrapper>
                    <Title
                      type={ThemeFontStyleTypeEnum.headingXLarge}
                      color={landingPageConfig?.rangeDesign.titleTextColor}
                      style={{
                        fontSize: 'clamp(2.25rem, 7vmin, 80px)',
                        wordBreak: 'keep-all',
                        lineHeight: 1.3
                      }}
                    >
                      {formatMessage({
                        id: 'storageone.designSolutionLandingPageTitleStartText'
                      })}
                      <br />
                      <span>
                        <b>{rangeName}</b>
                      </span>
                    </Title>
                  </TitleWrapper>
                )}
              </RangeTitleBackgroundContainer>
              <LandingPageGrid />
            </Grid>
            <CarouselContainer>
              <LandingPageGallery />
              {range === 'pax' && isMobile() && (
                <StyledCard
                  backgroundColor={landingPageConfig?.guide?.backgroundColor}
                  title={formatMessage({ id: 'storageone.landingPageGuideTitle' })}
                  body={formatMessage({
                    id: 'storageone.landingPageGuideDescription'
                  })}
                  data-testid="item-guide"
                  emphasised
                  ctaLabel={formatMessage({
                    id: 'storageone.landingPageBtnGuide'
                  })}
                  onClick={onGuideClick}
                  onKeyUp={e => e.key === 'Enter' && onGuideClick()}
                  buttonVariant="icon-primary-inverse"
                  compact={!isMobile()}
                  tabIndex={0}
                />
              )}
              {bookAppointmentEnabled && !isKiosk() && (
                <Box mt="l">
                  <StyledCard
                    title={formatMessage({
                      id: 'storageone.bookAPlanningSession'
                    })}
                    body={formatMessage({
                      id: 'storageone.bookAPlanningSessionLandingPageDescription'
                    })}
                    data-testid="card-book-planning-session"
                    emphasised
                    backgroundColor="#F5F5F5"
                    ctaLabel={formatMessage({
                      id: 'storageone.bookAPlanningSession'
                    })}
                    buttonVariant="icon-primary-inverse"
                    onClick={handleBookAppointment}
                    onKeyUp={e => e.key === 'Enter' && onOpenDesignClick()}
                    compact={!isMobile()}
                    tabIndex={0}
                  />
                </Box>
              )}
            </CarouselContainer>
          </GridContainer>
        </Container>
      </ContentMargins>
    </RtlStyles>
  );
};

export default LandingPage;
